import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Card } from '@/components/cards/Card'
import { Pill } from '@/components/pills/Pill'
import {
  fetchVisibilityByCid,
  updatePlaybookVisibility,
} from '@/reducers/playbooks/playbooks.actions'
import { clearVisibility } from '@/reducers/playbooks/playbooks.redux'
import { FilterComponent } from '@/components/filters/types/FilterComponent'

const getUserTagIds = (row, headers) =>
  headers.slice(1).flatMap((header) => {
    const tags = row[header.accessor]
    return Array.isArray(tags) ? tags.map((t) => t.id) : []
  })

const excludeTagMatchedAgents = (agentList, selectedTags, rows, headers) =>
  agentList.filter((agent) => {
    const row = rows.find((r) => r.agent_id === agent.value)
    if (!row) return true

    const agentTagIds = getUserTagIds(row, headers)
    return !agentTagIds.some((tagId) => selectedTags.some((tag) => tag.value === tagId))
  })

export const EditPlaybookVisibilityModal = ({ playbookCid, playbookName, onClose }) => {
  const dispatch = useDispatch()
  const [selectedTags, setSelectedTags] = useState([])
  const [grantedAgents, setGrantedAgents] = useState([])
  const [revokedAgents, setRevokedAgents] = useState([])
  const [showSelectedOnly, setShowSelectedOnly] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const { visibility, additionalLoading } = useSelector((state) => state.playbooks)

  const agents = useMemo(
    () =>
      visibility.rows.map((row) => ({
        value: row.agent_id,
        label: row.user_name,
        isAi: false,
      })),
    [visibility]
  )

  const tags = useMemo(() => {
    // Aggregate all unique tags from all users, grouped by category
    const tagsByCategory = {}

    // Skip first header which is "Name"
    visibility.headers.slice(1).forEach((header) => {
      tagsByCategory[header.label] = new Map() // Use Map to track unique tags by id
    })

    // Collect all tags from all users
    visibility.rows.forEach((row) => {
      visibility.headers.slice(1).forEach((header) => {
        const tagArray = row[header.accessor]
        if (Array.isArray(tagArray)) {
          tagArray.forEach((tag) => {
            if (!tagsByCategory[header.label].has(tag.id)) {
              tagsByCategory[header.label].set(tag.id, {
                value: tag.id,
                label: tag.label,
              })
            }
          })
        }
      })
    })

    // Return grouped structure with categories
    return Object.entries(tagsByCategory).map(([category, tagsMap]) => ({
      label: category,
      options: Array.from(tagsMap.values()),
    }))
  }, [visibility])

  useEffect(() => {
    if (playbookCid) {
      dispatch(fetchVisibilityByCid(playbookCid))
    }
  }, [playbookCid])

  // Initialize filters from config_visibility once, when fetched data first lands.
  // Use headers as the "data has arrived" signal — config_visibility is truthy
  // even in the empty initial Redux state, so it can't be used as the gate.
  useEffect(() => {
    if (initialized || isEmpty(visibility.headers)) {
      return
    }

    const { visibility_granted_users, visibility_tags, visibility_revoked_users } =
      visibility.config_visibility

    // Flatten grouped tags to find matching options
    const allTagOptions = tags.flatMap((group) => group.options)
    const selectedTagOptions = allTagOptions.filter((tag) => visibility_tags.includes(tag.value))
    setSelectedTags(selectedTagOptions)

    // Initialize granted agents, excluding any already covered by a selected tag
    const initGrantedAgents = agents.filter((agent) =>
      visibility_granted_users.includes(agent.value)
    )
    setGrantedAgents(
      excludeTagMatchedAgents(
        initGrantedAgents,
        selectedTagOptions,
        visibility.rows,
        visibility.headers
      )
    )

    // Initialize revoked agents
    const revokedAgentOptions = agents.filter((agent) =>
      visibility_revoked_users.includes(agent.value)
    )
    setRevokedAgents(revokedAgentOptions)
    setInitialized(true)
  }, [visibility, agents, tags, initialized])

  // Derived selected rows: granted agents + tag-matched, excluding revoked
  const selectedRows = useMemo(() => {
    return visibility.rows
      .filter((row) => {
        if (revokedAgents.some((a) => a.value === row.agent_id)) return false
        if (grantedAgents.some((a) => a.value === row.agent_id)) return true
        if (isEmpty(selectedTags)) return false
        return getUserTagIds(row, visibility.headers).some((tagId) =>
          selectedTags.some((tag) => tag.value === tagId)
        )
      })
      .map((row) => row.agent_id)
  }, [grantedAgents, selectedTags, revokedAgents, visibility])

  const formattedRows = useMemo(() => {
    return visibility.rows.map((row) => {
      const keys = Object.keys(row)
      const formattedRow = {}
      keys.forEach((key) => {
        if (!Array.isArray(row[key])) {
          formattedRow[key] = row[key]
        } else {
          formattedRow[key] = (
            <div className="playbook-tag-pill-container">
              {row[key].map((tag) => (
                <Pill
                  key={tag.id}
                  small
                  outlined
                  informative={selectedTags.some((t) => t.value === tag.id)}
                >
                  {tag.label}
                </Pill>
              ))}
            </div>
          )
        }
      })

      return formattedRow
    })
  }, [visibility, selectedTags])

  const displayedRows = useMemo(() => {
    if (!showSelectedOnly) return formattedRows
    return formattedRows.filter((row) => selectedRows.includes(row.agent_id))
  }, [formattedRows, selectedRows, showSelectedOnly])

  const columns = useMemo(
    () =>
      visibility.headers.map((header, index) =>
        index === 0 ? { ...header, isSearchable: true } : header
      ),
    [visibility]
  )

  const isTagMatched = (agentId) => {
    const row = visibility.rows.find((r) => r.agent_id === agentId)
    if (!row) return false
    return getUserTagIds(row, visibility.headers).some((tagId) =>
      selectedTags.some((tag) => tag.value === tagId)
    )
  }

  const handleBulkSelectSingle = (agentId) => {
    if (selectedRows.includes(agentId)) {
      // Unchecking: remove from granted, revoke if tag-matched
      setGrantedAgents((prev) => prev.filter((a) => a.value !== agentId))
      if (isTagMatched(agentId)) {
        setRevokedAgents((prev) => {
          if (prev.some((a) => a.value === agentId)) return prev
          const agent = agents.find((a) => a.value === agentId)
          return agent ? [...prev, agent] : prev
        })
      }
    } else {
      // Checking: un-revoke if revoked, grant if not tag-matched
      setRevokedAgents((prev) => prev.filter((a) => a.value !== agentId))
      if (!isTagMatched(agentId)) {
        setGrantedAgents((prev) => {
          if (prev.some((a) => a.value === agentId)) return prev
          const agent = agents.find((a) => a.value === agentId)
          return agent ? [...prev, agent] : prev
        })
      }
    }
  }

  const handleTagChange = (_, value) => {
    const newTags = value || []
    setSelectedTags(newTags)

    // Remove granted agents that are now covered by a selected tag
    setGrantedAgents((prev) =>
      excludeTagMatchedAgents(prev, newTags, visibility.rows, visibility.headers)
    )
  }

  const handleClose = () => {
    setSelectedTags([])
    setGrantedAgents([])
    setRevokedAgents([])
    setShowSelectedOnly(false)

    dispatch(clearVisibility())
    onClose()
  }

  const handleSubmit = async () => {
    const visibilitySettings = {
      visibility_granted_users: grantedAgents.map((a) => a.value),
      visibility_revoked_users: revokedAgents.map((a) => a.value),
      visibility_tags: selectedTags.map((t) => t.value),
    }

    const success = await dispatch(updatePlaybookVisibility(playbookCid, visibilitySettings))
    if (success) {
      handleClose()
    }
  }

  const handleSelectAll = () => {
    const allTagOptions = tags.flatMap((group) => group.options)
    setSelectedTags(allTagOptions)
    setGrantedAgents(
      excludeTagMatchedAgents(agents, allTagOptions, visibility.rows, visibility.headers)
    )
    setRevokedAgents([])
  }

  const handleDeselectAll = () => {
    setShowSelectedOnly(false)
    setSelectedTags([])
    setGrantedAgents([])
    setRevokedAgents([])
  }

  return (
    <BasicModal
      title={`Visibility Settings [${playbookName.trim()}]`}
      onClose={handleClose}
      show
      size="large"
    >
      <p className="modal-subtitle">Choose who can access this playbook in the agent app.</p>
      <div className="modal-header-container">
        <FilterComponent
          type="multiSelect"
          loading={false}
          disabled={additionalLoading.visibility || isEmpty(tags)}
          schema={{
            accessor: 'tags',
            label: 'Tags',
            options: tags,
          }}
          value={selectedTags}
          updateSearch={handleTagChange}
          isClearable={false}
        />
      </div>
      <div className="modal-content-wrapper">
        <Card className="no-padding table-segment">
          <AdvancedTable
            loading={additionalLoading.visibility}
            rows={displayedRows}
            columns={columns}
            pagination
            index="agent_id"
            defaultOrder="asc"
            defaultOrderBy="user_name"
            bulkSelectable
            selectedRows={selectedRows}
            handleSelectAll={handleSelectAll}
            handleDeselectAll={handleDeselectAll}
            handleBulkSelectSingle={handleBulkSelectSingle}
            bulkSelectAction={
              <Button size="small" basic onClick={() => setShowSelectedOnly((prev) => !prev)}>
                {showSelectedOnly ? 'Show All Agents' : 'Show Selected Only'}
              </Button>
            }
          />
        </Card>
      </div>
      <div className="modal-footer">
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          primary
          onClick={handleSubmit}
          loading={additionalLoading.visibilityUpdate}
          disabled={additionalLoading.visibilityUpdate || additionalLoading.visibility}
        >
          Confirm
        </Button>
      </div>
    </BasicModal>
  )
}
